import { AlarmClock, Ban, CalendarCheck2, ClipboardCheck, History, RotateCcw, Settings, Wrench } from 'lucide-react';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { usePermission } from '../../../../auth/AuthContext';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { CountBadge } from '../../../../components/CountBadge';
import { ErrorState } from '../../../../components/ErrorState';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { Menu } from '../../../../components/Menu';
import { PreviewTable } from '../../../../components/PreviewTable';
import { ProfileBanner, type ProfileBannerTone } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { Skeleton } from '../../../../components/Skeleton';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly, formatDateTime } from '../../../../lib/formatDate';
import { personsApi } from '../../../human-resources/personnel/personnelApi';
import { maintenanceItemsApi } from '../buildingManagementApi';
import { MAINTENANCE_AGING, type MaintenanceItemDetail } from '../buildingManagementTypes';
import { AgingStatus } from '../components/AgingStatus';
import { MaintenanceItemModal } from '../components/MaintenanceItemModal';
import { MaintenanceRecordsPanel } from '../components/MaintenanceRecordsPanel';
import { RecordMaintenanceModal } from '../components/RecordMaintenanceModal';
import { maintenanceItemTitle } from '../utils/maintenanceItemTitle';

type TabKey = 'info' | 'records';

/**
 * One maintenance item — what it is, when it is due, and what has been done.
 *
 * On the **header** layout with two tabs: the item's own fields, and its maintenance history.
 * It LANDS on Info — a reader opens an item to check when it is due and who answers for it.
 *
 * **The banner says where the item stands, for every band** — on schedule as much as expired —
 * and owns the record-level action, Record Maintenance, which opens a form (so it carries no
 * confirmation of its own; the form's tick is the gate).
 */
export function MaintenanceItemProfilePage() {
  const { id } = useParams<{ id: string }>();
  const canReadPeople = usePermission('personnel.read');
  const toast = useToast();

  const [item, setItem] = useState<MaintenanceItemDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [tab, setTab] = useState<TabKey>('info');
  const [editing, setEditing] = useState(false);
  const [recording, setRecording] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [recordsKey, setRecordsKey] = useState(0);

  /*
    THE RESPONSIBLE EMPLOYEE'S NAME, in its own call and only where there is one to resolve —
    the identity split. A failure leaves it unresolved and the page standing.
  */
  const [responsibleName, setResponsibleName] = useState<string | null>(null);

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      maintenanceItemsApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;

          setItem(next);
          setError(null);

          // `== null`, never `===`: a null property is omitted from the wire and arrives undefined.
          if (next.responsiblePersonId == null || !canReadPeople) {
            setResponsibleName(null);
            return;
          }

          personsApi
            .resolve([next.responsiblePersonId], signal)
            .then((people) => {
              if (signal?.aborted) return;
              setResponsibleName(people[0]?.displayName ?? null);
            })
            .catch(() => setResponsibleName(null));
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this item.', 0));
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id, canReadPeople],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  /** Puts a deleted item back. An undo — no tick and no password. */
  async function restore() {
    if (!item) return;

    setRestoring(true);
    setError(null);

    try {
      await maintenanceItemsApi.restore(item.id);
      toast.success(`Restored ${item.referenceNumber}.`);
      load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('Could not restore this item.', 0));
    } finally {
      setRestoring(false);
    }
  }

  const breadcrumb: BreadcrumbSegment[] = [
    'General Services',
    'Building Management',
    { label: 'Maintenance', to: '/general-services/building-management/maintenance' },
    item?.referenceNumber ?? 'Item',
  ];

  // The full-page error is for a failure with NOTHING to show; a failed refresh keeps the page.
  if (error && !item) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} onRetry={() => load()} />
      </div>
    );
  }

  if (loading && !item) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-4 p-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-64 w-full max-w-4xl" />
        </div>
      </div>
    );
  }

  if (!item) return null;

  const banner = bannerFor(item);

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell<TabKey>
        id="maintenance-item-profile"
        panel={tab === 'records' ? 'bleed' : 'cards'}
        header={
          <ProfileHeader
            id="maintenance-item-profile-header"
            icon={<Wrench className="h-6 w-6" aria-hidden="true" />}
            // The title IS the category and building, so the meta line does not repeat them.
            title={maintenanceItemTitle(item)}
            meta={[item.referenceNumber, item.recurrenceLabel]}
            status={<AgingStatus aging={item.aging} label={item.agingLabel} isActive={item.isActive} />}
            createdBy={item.createdByName}
            createdAt={item.createdAt}
            actions={
              <Menu
                id="maintenance-item-profile-cog"
                items={[
                  {
                    key: 'history',
                    label: 'History Logs',
                    icon: <History className="h-4 w-4" aria-hidden="true" />,
                    permission: 'audit-logs.read',
                    onClick: () => setHistoryOpen(true),
                  },
                ]}
                trigger={({ open, toggle }) => (
                  <button
                    type="button"
                    onClick={toggle}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    aria-label="Item options"
                    className="rounded-lg p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                  >
                    <Settings className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              />
            }
          />
        }
        banner={
          error ? (
            <ProfileBanner
              id="maintenance-item-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message={error.message}
            />
          ) : !item.isActive ? (
            <ProfileBanner
              id="maintenance-item-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This item is deleted. It is off the register, the calendar and the aging counts; its records are kept."
              action={
                <PermissionGate permission="building-management.delete">
                  <Button
                    id="maintenance-item-profile-restore"
                    variant="secondary"
                    leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => void restore()}
                    disabled={restoring}
                    loading={restoring}
                    loadingLabel="Restoring"
                  >
                    Restore
                  </Button>
                </PermissionGate>
              }
            />
          ) : (
            <ProfileBanner
              id="maintenance-item-profile-banner"
              tone={banner.tone}
              icon={banner.icon}
              message={banner.message}
              action={
                <PermissionGate permission="building-management.record">
                  <Button
                    id="maintenance-item-profile-record"
                    variant={banner.tone === 'emerald' ? 'secondary' : 'primary'}
                    leading={<ClipboardCheck className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => setRecording(true)}
                  >
                    Record activity
                  </Button>
                </PermissionGate>
              }
            />
          )
        }
        tabs={[
          { key: 'info', label: 'Info' },
          {
            key: 'records',
            label: (
              <span className="inline-flex items-center gap-1.5">
                Activities <CountBadge count={item.recordCount} />
              </span>
            ),
          },
        ]}
        activeTab={tab}
        onTabChange={setTab}
      >
        {tab === 'info' ? (
          <>
            <ProfileCard
              id="maintenance-item-profile-details"
              title="Details"
              onEdit={() => setEditing(true)}
              editPermission="building-management.update"
            >
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Reference', value: item.referenceNumber, mono: true },
                  { label: 'Category', value: item.categoryName },
                  {
                    label: 'Building',
                    value: item.buildingName != null ? [item.buildingCode, item.buildingName].filter(Boolean).join(' — ') : null,
                  },
                  { label: 'Location', value: item.location },
                  { label: 'Recurrence', value: item.recurrenceLabel },
                  { label: 'Due date', value: formatDateOnly(item.dueDate), mono: true },
                  { label: 'Days until due', value: String(item.daysUntilDue), mono: true },
                  {
                    label: 'Near Expiration window',
                    value: `${item.nearExpirationDays} days, ${
                      item.nearExpirationFromCategory ? "from the category's own window" : 'from Settings › Aging'
                    }`,
                  },
                  {
                    label: 'Last performed',
                    value: item.lastPerformedOn != null ? formatDateOnly(item.lastPerformedOn) : null,
                    mono: true,
                  },
                  {
                    /*
                      `Hidden` is NOT an em dash. A reader without `personnel.read` is being told the
                      item HAS a responsible employee and this page has not been given the name.
                    */
                    label: 'Responsible employee',
                    value: item.responsiblePersonnelId == null ? null : canReadPeople ? responsibleName : 'Hidden',
                  },
                  { label: 'Contractor', value: item.supplierName },
                  { label: 'Remarks', value: item.remarks },
                ]}
              />
            </ProfileCard>

            <ProfileCard id="maintenance-item-profile-audit" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Created by', value: item.createdByName },
                  { label: 'Created', value: formatDateTime(item.createdAt), mono: true },
                  { label: 'Updated by', value: item.updatedByName },
                  { label: 'Updated', value: item.updatedAt ? formatDateTime(item.updatedAt) : null, mono: true },
                ]}
              />
            </ProfileCard>
          </>
        ) : (
          <div className="flex h-full min-h-0 flex-col p-4">
            <MaintenanceRecordsPanel
              key={recordsKey}
              item={item}
              onRecord={() => setRecording(true)}
              onChanged={() => load()}
            />
          </div>
        )}
      </ProfileHeaderShell>

      {editing ? (
        <MaintenanceItemModal
          open
          editing={item}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}

      {recording ? (
        <RecordMaintenanceModal
          open
          item={item}
          onClose={() => setRecording(false)}
          onSaved={() => {
            setRecording(false);
            setRecordsKey((k) => k + 1);
            load();
          }}
        />
      ) : null}

      <HistoryLogsModal open={historyOpen} onClose={() => setHistoryOpen(false)} module="building-management" entityId={item.id} />
    </div>
  );
}

/**
 * The banner for a live item, one per band — it renders for every state, not only the bad ones,
 * so a reader learns to read the strip rather than skip it. Amber is legal here: a profile is not
 * an index page.
 */
function bannerFor(item: MaintenanceItemDetail): {
  tone: ProfileBannerTone;
  icon: ReactNode;
  message: string;
} {
  const due = formatDateOnly(item.dueDate);
  const days = Math.abs(item.daysUntilDue);
  const plural = days === 1 ? 'day' : 'days';

  switch (item.aging) {
    case MAINTENANCE_AGING.onSchedule:
      return {
        tone: 'emerald',
        icon: <CalendarCheck2 className="h-4 w-4" aria-hidden="true" />,
        message: `On Schedule — next due ${due}, in ${days} ${plural}.`,
      };
    case MAINTENANCE_AGING.nearExpiration:
      return {
        tone: 'amber',
        icon: <AlarmClock className="h-4 w-4" aria-hidden="true" />,
        message:
          item.daysUntilDue === 0
            ? `Near Expiration — due today, ${due}.`
            : `Near Expiration — due ${due}, in ${days} ${plural}.`,
      };
    case MAINTENANCE_AGING.expired:
      return {
        tone: 'red',
        icon: <AlarmClock className="h-4 w-4" aria-hidden="true" />,
        message: `Expired — it was due ${due}, ${days} ${plural} ago.`,
      };
    case MAINTENANCE_AGING.expiredBeyondThreshold:
      return {
        tone: 'red',
        icon: <Ban className="h-4 w-4" aria-hidden="true" />,
        message: `${item.agingLabel} — it was due ${due}, ${days} ${plural} ago.`,
      };
  }
}
